import { useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import ScrollCta from '@/components/base/scroll-cta'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPImage, WPMedia, WPPageData } from '@/types'

type JournalPagePost = {
  id: number
  date: string
  link: string
  title: { rendered: string }
  excerpt: { rendered: string }
  featured_image?: Partial<WPImage>
  featured_image_mobile?: Partial<WPImage>
  categories: number[]
  template: string
  type: string
}

type CategoryFilter = {
  id: number
  title: string
  slug: string
  count: number
}

type YearFilterItem = {
  id: number
  title: string
  slug: string
  count: number
}

type YearFilter = {
  id: null
  title: string
  slug: 'year'
  items: YearFilterItem[]
}

type PostsFilter = CategoryFilter | YearFilter

type JournalPageProps = {
  pageProps: WPPageData & {
    acf?: {
      hero?: {
        title?: string
        location?: string
        media?: WPMedia
      }
      credits?: {
        role: string
        name: string
      }[]
    }
    posts?: JournalPagePost[]
    posts_filter?: PostsFilter[]
  }
}

const isYearFilter = (filter: PostsFilter): filter is YearFilter =>
  filter.slug === 'year'

const getPostYear = (dateString: string): string => {
  const date = new Date(dateString)
  return String(date.getFullYear())
}

const formatPostDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const JournalPage = ({ pageProps }: JournalPageProps) => {
  const rootRef = useRef<HTMLDivElement>(null!)
  const yearFilterRef = useRef<HTMLDivElement>(null!)
  const yearDropdownRef = useRef<HTMLDivElement>(null!)
  const firstAnimationRef = useRef(true)
  const lastYearTriggerPointerTypeRef = useRef<string | null>(null)
  const { strings } = useStore()

  const title = pageProps.title.rendered
  const posts = useMemo(() => pageProps.posts || [], [pageProps.posts])
  const postsFilter = useMemo(
    () => pageProps.posts_filter || [],
    [pageProps.posts_filter]
  )

  const categoryFilters = useMemo(
    () => postsFilter.filter((f): f is CategoryFilter => !isYearFilter(f)),
    [postsFilter]
  )

  const yearFilter = useMemo(
    () => postsFilter.find(isYearFilter) || null,
    [postsFilter]
  )

  const [activeCategoryFilter, setActiveCategoryFilter] =
    useState<string>('all')
  const [activeYearFilter, setActiveYearFilter] = useState<string | null>(null)
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const filteredPosts = useMemo(() => {
    let nextPosts = posts

    if (activeYearFilter) {
      nextPosts = nextPosts.filter(
        (post) => getPostYear(post.date) === activeYearFilter
      )
    }

    if (activeCategoryFilter === 'all') return nextPosts

    const matchingCategory = categoryFilters.find(
      (f) => f.slug === activeCategoryFilter
    )
    if (!matchingCategory) return nextPosts

    return nextPosts.filter((post) =>
      post.categories.includes(matchingCategory.id)
    )
  }, [posts, activeYearFilter, activeCategoryFilter, categoryFilters])

  const filteredPostsKey = useMemo(
    () => filteredPosts.map((p) => p.id).join('|'),
    [filteredPosts]
  )

  // Fade-in posts on filter change
  useGSAP(
    () => {
      const cards = gsap.utils.toArray<HTMLElement>(
        '[data-journal-card]',
        rootRef.current
      )
      if (!cards.length) return

      if (firstAnimationRef.current) {
        firstAnimationRef.current = false
        gsap.set(cards, { opacity: 1 })
        return
      }

      gsap.fromTo(
        cards,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.35,
          ease: 'power2.out',
          stagger: 0.03
        }
      )
    },
    {
      scope: rootRef,
      dependencies: [filteredPostsKey]
    }
  )

  // Year dropdown animation
  useGSAP(
    () => {
      if (!yearDropdownRef.current) return

      gsap.to(yearDropdownRef.current, {
        opacity: yearDropdownOpen ? 1 : 0,
        y: yearDropdownOpen ? 0 : -8,
        pointerEvents: yearDropdownOpen ? 'auto' : 'none',
        duration: 0.2,
        ease: 'power2.out'
      })
    },
    {
      dependencies: [yearDropdownOpen]
    }
  )

  // Close year dropdown on Escape
  const handleYearKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setYearDropdownOpen(false)
    }
  }

  useClickAway(yearFilterRef, () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (yearDropdownOpen) setYearDropdownOpen(false)
  })

  const handleResetFilters = () => {
    setActiveCategoryFilter('all')
    setActiveYearFilter(null)
    setYearDropdownOpen(false)
  }

  const handleCategoryFilterClick = (slug: string) => {
    setActiveCategoryFilter(slug)
    setYearDropdownOpen(false)
  }

  const handleYearFilterClick = (slug: string) => {
    setActiveYearFilter(slug)
    setYearDropdownOpen(false)
  }

  const handleYearPointerEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setYearDropdownOpen(true)
  }

  const handleYearPointerLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setYearDropdownOpen(false)
      closeTimerRef.current = null
    }, 200)
  }

  const handleYearTriggerPointerDown = (
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    lastYearTriggerPointerTypeRef.current = event.pointerType
  }

  const handleYearTriggerClick = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (lastYearTriggerPointerTypeRef.current === 'mouse') {
      return
    }

    setYearDropdownOpen((prev) => !prev)
  }

  const handleYearContainerPointerEnter = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (event.pointerType !== 'mouse') return

    handleYearPointerEnter()
  }

  const handleYearContainerPointerLeave = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (event.pointerType !== 'mouse') return

    handleYearPointerLeave()
  }

  const isYearActive = yearFilter?.items.some(
    (item) => item.slug === activeYearFilter
  )

  const isAllFiltersActive =
    activeCategoryFilter === 'all' && activeYearFilter === null

  return (
    <Section ref={rootRef} data-layout="journal">
      {/* Hero title */}
      <Spacer variant="xxl" />
      <Container className="mb-spacing-xs lg:mb-spacing-2xl">
        <div className="grid grid-cols-2 gap-x-gutter md:grid-cols-6 lg:grid-cols-12">
          <div className="col-span-2 flex items-end justify-between md:col-span-3 lg:col-span-12">
            <Typography
              variant="huge"
              component="h1"
              className="hidden lg:block"
            >
              {title}
            </Typography>
            <Typography
              variant="h1"
              component="span"
              aria-hidden="true"
              className="block lg:hidden"
            >
              {title}
            </Typography>
            <ScrollCta />
          </div>
        </div>
      </Container>

      {/* Filters */}
      <Container>
        <nav
          aria-label={strings?.labels?.journal_filters || 'Filtri articoli'}
          className="flex items-center justify-start gap-[16px] md:justify-end"
        >
          <button
            type="button"
            onClick={handleResetFilters}
            className={cn(
              'px-[8px] transition-opacity duration-200',
              isAllFiltersActive ? 'opacity-100' : 'opacity-30'
            )}
            aria-pressed={isAllFiltersActive}
          >
            <Typography variant="regular" className="condensed">
              {strings?.labels?.journal_filter_all || 'All'}
            </Typography>
          </button>

          {categoryFilters.map((filter) => (
            <button
              key={filter.slug}
              type="button"
              onClick={() => handleCategoryFilterClick(filter.slug)}
              className={cn(
                'px-[8px] transition-opacity duration-200',
                activeCategoryFilter === filter.slug
                  ? 'opacity-100'
                  : 'opacity-30'
              )}
              aria-pressed={activeCategoryFilter === filter.slug}
            >
              <Typography variant="regular" className="condensed">
                {filter.title}
              </Typography>
            </button>
          ))}

          {yearFilter ? (
            <div
              ref={yearFilterRef}
              className="relative"
              onPointerEnter={handleYearContainerPointerEnter}
              onPointerLeave={handleYearContainerPointerLeave}
              onKeyDown={handleYearKeyDown}
            >
              <button
                type="button"
                onPointerDown={handleYearTriggerPointerDown}
                onClick={handleYearTriggerClick}
                aria-expanded={yearDropdownOpen}
                aria-controls="journal-year-dropdown"
                className={cn(
                  'px-[8px] transition-opacity duration-200',
                  isYearActive ? 'opacity-100' : 'opacity-30'
                )}
              >
                <Typography variant="regular" className="condensed">
                  {isYearActive
                    ? activeYearFilter
                    : strings?.labels?.journal_filter_year || 'Year'}
                </Typography>
              </button>

              <div
                id="journal-year-dropdown"
                ref={yearDropdownRef}
                role="menu"
                aria-hidden={!yearDropdownOpen}
                className="pointer-events-none absolute right-0 top-full z-20 mt-[4px] flex flex-col gap-[4px] bg-background-frame-light p-[12px] opacity-0 dark:bg-background-frame-dark"
              >
                {yearFilter.items.map((yearItem) => (
                  <button
                    key={yearItem.slug}
                    type="button"
                    role="menuitem"
                    onClick={() => handleYearFilterClick(yearItem.slug)}
                    className={cn(
                      'whitespace-nowrap px-[8px] py-[4px] text-left transition-opacity duration-200',
                      activeYearFilter === yearItem.slug
                        ? 'opacity-100'
                        : 'opacity-60'
                    )}
                  >
                    <Typography variant="regular" className="condensed">
                      {yearItem.title}
                    </Typography>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </nav>
      </Container>
      <Spacer variant="xxl" />

      {/* Post grid */}
      <Container className="mx-auto max-w-[1640px]">
        <Grid className="gap-y-spacing-2xl lg:gap-y-[224px]">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, i) => {
              return (
                <div
                  key={`post-${i}`}
                  className={cn('col-span-2 md:col-span-3 lg:col-span-4', {
                    'lg:col-start-5': i % 8 === 0,
                    'lg:col-start-9': i % 8 === 3 || i % 8 === 7,
                    'lg:col-start-1': i % 8 === 6
                  })}
                >
                  <JournalPageCard
                    key={post.id}
                    post={post}
                    categoryFilters={categoryFilters}
                  />
                </div>
              )
            })
          ) : (
            <div className="col-span-2 md:col-span-6 lg:col-span-12">
              <Typography variant="h5">
                {strings?.labels?.no_results || 'No results'}
              </Typography>
            </div>
          )}
        </Grid>
      </Container>
      <Spacer variant="xxl" />
    </Section>
  )
}

type JournalPageCardProps = {
  post: JournalPagePost
  categoryFilters: CategoryFilter[]
}

const JournalPageCard = ({ post, categoryFilters }: JournalPageCardProps) => {
  const imageAlt = post.featured_image?.alt || post.title.rendered

  const categoryLabel =
    post.categories.length > 0
      ? categoryFilters.find((f) => post.categories.includes(f.id))?.title || ''
      : ''

  const formattedDate = formatPostDate(post.date)

  const imageUrl =
    post.featured_image_mobile?.sizes?.large?.url ||
    post.featured_image_mobile?.sizes?.medium?.url ||
    post.featured_image_mobile?.url ||
    post.featured_image?.sizes?.large?.url ||
    post.featured_image?.sizes?.medium?.url ||
    post.featured_image?.url

  return (
    <article data-journal-card className="opacity-0">
      <Link href={post.link} scroll={false} className="group/card block">
        {imageUrl ? (
          <div className="relative aspect-[3/4] w-full overflow-hidden">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="h-full w-full object-cover transition-transform duration-500 ease-out-cubic group-hover/card:scale-105"
            />
          </div>
        ) : (
          <div className="aspect-[3/4] w-full bg-background-frame-light dark:bg-background-frame-dark" />
        )}

        <div className="mt-spacing-sm">
          <div className="flex items-center justify-between gap-spacing-xs">
            <Typography variant="h5" component="h2">
              {post.title.rendered}
            </Typography>
            <SVGIcon
              symbol="cta-arrow"
              className="size-[24px] shrink-0 transition-transform duration-300 ease-out-cubic group-hover/card:rotate-45"
              aria-hidden="true"
            />
          </div>

          <div className="mt-spacing-sm flex items-center justify-between gap-spacing-xs text-text-primary-light">
            {categoryLabel ? (
              <Typography
                variant="regular"
                className="condensed text-text-primary-light"
              >
                {categoryLabel}
              </Typography>
            ) : null}
            {categoryLabel ? (
              <span
                className="inline-block h-[4px] w-[4px] rounded-full bg-current opacity-30"
                aria-hidden="true"
              />
            ) : null}
            <Typography variant="regular" className="condensed opacity-50">
              {formattedDate}
            </Typography>
          </div>
        </div>
      </Link>
    </article>
  )
}

export default JournalPage
